import React, { useEffect, useState } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import WithdrawProfitChart from './WithdrawProfitChart';
import { getWithdrawals, getWithdrawalsSummary } from '../../services/WithdrawalsService';
import { processError } from '../../services/ServiceUtils';
import { getEmptyMessage, getErrorMessage } from '../../utils/MessageUtils';

// Histórico de Saques (menu Caixa). Consome o ledger de auditoria: relatório/gráfico de lucro colhido
// no topo + tabela das tentativas de saque (REAL/DRY-RUN/FALHA). Filtro por status.
function statusBadge(status) {
    if (status === 'SUCCESS') return <span className='badge bg-success'>Executado</span>;
    if (status === 'DRYRUN') return <span className='badge bg-info'>Simulado</span>;
    if (status === 'FAILED') return <span className='badge bg-danger'>Falhou</span>;
    return <span className='badge bg-secondary'>{status}</span>;
}

function fmtDate(ts) {
    return new Date(ts).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function WithdrawHistory() {
    const [summary, setSummary] = useState(null);
    const [rows, setRows] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [notification, setNotification] = useState(getEmptyMessage());

    function load() {
        getWithdrawalsSummary()
            .then(setSummary)
            .catch(err => setNotification(getErrorMessage(processError(err))));
        getWithdrawals({ status: statusFilter || undefined, limit: 100 })
            .then(result => setRows(result.rows || []))
            .catch(err => setNotification(getErrorMessage(processError(err))));
    }

    useEffect(load, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const quoteSymbol = (rows[0] && rows[0].quoteSymbol) || 'USDT';

    return (
        <React.Fragment>
            <Menu />
            <main className='content'>
                <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4'>
                    <div className='d-block mb-4 mb-md-0'>
                        <h2 className='h4'>Histórico de Saques</h2>
                        <p className='text-muted small mb-0'>Auditoria de todos os saques e análise do lucro colhido ao longo do tempo.</p>
                    </div>
                </div>

                {/* Cartões de resumo */}
                <div className='row'>
                    <div className='col-12 col-sm-6 col-xl-3 mb-4'>
                        <div className='card border-0 shadow'>
                            <div className='card-body'>
                                <h6 className='text-muted mb-1'>Lucro colhido (total)</h6>
                                <h4 className='mb-0 text-success'>{summary ? summary.totalSkimmedQuote.toFixed(2) : '—'} {quoteSymbol}</h4>
                                <small className='text-muted'>Realização de lucro (skim) já sacada</small>
                            </div>
                        </div>
                    </div>
                    <div className='col-12 col-sm-6 col-xl-3 mb-4'>
                        <div className='card border-0 shadow'>
                            <div className='card-body'>
                                <h6 className='text-muted mb-1'>Total sacado</h6>
                                <h4 className='mb-0'>{summary ? summary.totalWithdrawnQuote.toFixed(2) : '—'} {quoteSymbol}</h4>
                                <small className='text-muted'>Todos os saques reais</small>
                            </div>
                        </div>
                    </div>
                    <div className='col-12 col-sm-6 col-xl-3 mb-4'>
                        <div className='card border-0 shadow'>
                            <div className='card-body'>
                                <h6 className='text-muted mb-1'>Lucro realizado atual</h6>
                                <h4 className='mb-0'>{summary && summary.latestRealized != null ? Number(summary.latestRealized).toFixed(2) : '—'} {quoteSymbol}</h4>
                                <small className='text-muted'>Marca d'água da realização de lucro</small>
                            </div>
                        </div>
                    </div>
                    <div className='col-12 col-sm-6 col-xl-3 mb-4'>
                        <div className='card border-0 shadow'>
                            <div className='card-body'>
                                <h6 className='text-muted mb-1'>Saques</h6>
                                <h4 className='mb-0'>{summary ? summary.totalWithdrawals : '—'}</h4>
                                <small className='text-muted'>
                                    {summary ? `${summary.countByStatus.SUCCESS} reais · ${summary.countByStatus.DRYRUN} simulados · ${summary.countByStatus.FAILED} falhas` : ''}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Gráfico de lucro colhido acumulado */}
                {summary && <WithdrawProfitChart series={summary.series} quoteSymbol={quoteSymbol} />}

                {/* Tabela com filtro por status */}
                <div className='card card-body border-0 shadow table-wrapper table-responsive'>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                        <h6 className='mb-0'>Saques</h6>
                        <select className='form-select w-auto' value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                            <option value=''>Todos os status</option>
                            <option value='SUCCESS'>Executados</option>
                            <option value='DRYRUN'>Simulados</option>
                            <option value='FAILED'>Falhas</option>
                        </select>
                    </div>
                    <table className='table table-hover'>
                        <thead>
                            <tr>
                                <th className='border-gray-200'>Data</th>
                                <th className='border-gray-200'>Moeda</th>
                                <th className='border-gray-200'>Quantidade</th>
                                <th className='border-gray-200'>Valor (~{quoteSymbol})</th>
                                <th className='border-gray-200'>Tipo</th>
                                <th className='border-gray-200'>Status</th>
                                <th className='border-gray-200'>Ambiente</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows && rows.length
                                ? rows.map(w => (
                                    <tr key={w.id}>
                                        <td>{fmtDate(w.createdAt)}</td>
                                        <td>{w.coin}{w.network ? <span className='text-muted small'> · {w.network}</span> : ''}</td>
                                        <td>{Number(w.amount)}</td>
                                        <td>{w.quoteValue != null ? Number(w.quoteValue).toFixed(2) : '—'}</td>
                                        <td>{w.isSkim ? <span className='badge bg-success-soft text-success'>Realização de lucro</span> : <span className='text-muted'>Saque</span>}</td>
                                        <td>
                                            {statusBadge(w.status)}
                                            {w.status === 'FAILED' && w.error ? <div className='text-danger small mt-1' title={w.error}>{String(w.error).slice(0, 80)}</div> : ''}
                                        </td>
                                        <td><span className='text-muted small'>{w.environment || '—'}</span></td>
                                    </tr>
                                ))
                                : <tr><td colSpan='7' className='text-center text-muted py-4'>Nenhum saque registrado ainda.</td></tr>
                            }
                        </tbody>
                    </table>
                </div>
                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    )
}

export default WithdrawHistory;
